import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Sparkles, Phone, Mail } from 'lucide-react';
import { FadeUpOnScroll, ScaleOnScroll } from './ScrollReveal';
import { GradientText } from './AnimatedText';
import { ConfettiButton, triggerCelebration } from './ConfettiButton';
import { AuroraBackground } from './AuroraBackground';

export const FinalCTASectionUltimate = () => {
  const handleCTAClick = () => {
    triggerCelebration();
    setTimeout(() => {
      window.location.href = '/register';
    }, 1500);
  };

  return (
    <section
      id="cta"
      data-testid="final-cta-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <AuroraBackground className="absolute inset-0">
        <div className="absolute inset-0" />
      </AuroraBackground>
      
      {/* Background Pattern */}
      <div 
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScaleOnScroll>
          <div 
            className="relative bg-[#111C32]/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8 md:p-16 text-center overflow-hidden"
            style={{ boxShadow: '0 30px 100px -20px rgba(0,0,0,0.6)' }}
          >
            {/* Decorative Gradient */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at top, rgba(47,128,255,0.1) 0%, transparent 50%)'
              }}
            />

            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 rounded-full px-4 py-2 mb-8"
            >
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-sm font-medium">Starten Sie noch heute</span>
            </motion.div>

            {/* Headline */}
            <FadeUpOnScroll delay={0.1}>
              <h2 
                className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Bereit für das <GradientText>modernste</GradientText> Kassensystem?
              </h2>
            </FadeUpOnScroll>

            <FadeUpOnScroll delay={0.2}>
              <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-10">
                Testen Sie Cash-X 14 Tage kostenlos und unverbindlich. 
                Keine Kreditkarte erforderlich. Keine versteckten Kosten.
              </p>
            </FadeUpOnScroll>

            {/* CTAs */}
            <FadeUpOnScroll delay={0.3}>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <ConfettiButton
                  data-testid="final-cta-primary-button"
                  className="bg-gradient-to-r from-[#2F80FF] to-[#2DD4BF] hover:from-[#4A92FF] hover:to-[#34D9C4] text-white px-10 h-14 text-lg font-semibold rounded-xl shadow-lg shadow-blue-500/25 border-0"
                  onClick={handleCTAClick}
                >
                  Jetzt kostenlos starten
                  <ArrowRight className="ml-2 h-5 w-5" />
                </ConfettiButton>
                
                <Button
                  data-testid="final-cta-secondary-button"
                  variant="outline"
                  className="border-slate-600 text-slate-200 hover:bg-white/5 h-14 px-8 text-base font-medium rounded-xl"
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Preise ansehen
                </Button>
              </div>
            </FadeUpOnScroll>

            {/* Trust Points */}
            <FadeUpOnScroll delay={0.4}>
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  14 Tage kostenlos
                </span>
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Keine Kreditkarte nötig
                </span>
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Monatlich kündbar
                </span>
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  TSE inklusive
                </span>
              </div>
            </FadeUpOnScroll>

            {/* Contact Options */}
            <FadeUpOnScroll delay={0.5}>
              <div className="mt-12 pt-8 border-t border-slate-700/50">
                <p className="text-slate-500 text-sm mb-4">Lieber persönlich beraten lassen?</p>
                <div className="flex flex-wrap items-center justify-center gap-6">
                  <a 
                    href="tel:+4989123456789"
                    className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span>+49 89 123 456 789</span>
                  </a>
                  <a 
                    href="mailto:info@cash-x.de"
                    className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span>info@cash-x.de</span>
                  </a>
                </div>
              </div>
            </FadeUpOnScroll>
          </div>
        </ScaleOnScroll>
      </div>
    </section>
  );
};

export default FinalCTASectionUltimate;
